import { Link, useNavigate, useParams } from "react-router-dom"
import useTargetStorage from "@/hooks/useTargetStorage"
import Callout from "components/callout/callout"
import { useTranslation } from "react-i18next"
import ActionsList from "features/storages/products/actions/actionList"
import AmountTag from "components/tags/amountTag"
import Tag from "components/tags/tag"
import CustomInputDataDisplay from "features/storages/categories/customInputs/customInputDataDisplay"
import useProducts from "hooks/useProducts"
import useLayout from "hooks/useLayout"
import useCategories from "hooks/useCategories"
import useLocations from "hooks/useLocations"
import useStorages from "hooks/useStorages"
import CategoryTags from "components/tags/categoryTags"
import LocationTags from "components/tags/locationTags"
import Section from "components/layout/section"
import SectionHeader from "components/layout/sectionHeader"
import SectionHeaderTitle from "components/layout/sectionHeaderTitle"
import RightSidebar from "components/sidebar/right/RightSidebar"
import SidebarGroup from "components/sidebar/sidebarGroup"
import SidebarRow from "components/sidebar/SidebarRow"
import { ReactComponent as EditIcon } from 'assets/icons/edit.svg'
import { ReactComponent as DeleteIcon } from 'assets/icons/delete.svg'
import { ReactComponent as MoreVertIcon } from 'assets/icons/more_vert.svg'
import SectionDivider from "components/layout/sectionDivider"
import SectionHeaderActions from "components/layout/sectionHeaderActions"
import FloatingButtonGroup from "components/buttons/floatingButtonGroup"
import FloatingButtonGroupButton from "components/buttons/FloatingButtonGroupButton"
import Button from "components/inputs/button"
import { ReactComponent as HistoryIcon } from 'assets/icons/restore.svg'
import { ReactComponent as AddIcon } from 'assets/icons/add.svg'
import { ReactComponent as ArrowDropDownIcon } from 'assets/icons/arrow_drop_down.svg'
import PageTitle from "features/navigation/pageTitle"
import ProductImageGallery from "@/components/image/ProductImageGallery"
import { paramGuardStorage, paramGuardProduct } from "@/utils/paramGuard"
import FloatingButtonGroupHr from "@/components/buttons/FloatingButtonGroupHr"
import IconButton from "@/components/buttons/iconButton"
import { AccessRole, DeviceSize, FormStatus } from "@/types"
import useWindowDimensions from "@/hooks/useWindowDimensions"
import { useState } from "react"
import DeletedDocCallout from "@/components/callout/deletedDocCallout"
import UserCard from "components/user/UserCard"
import ProductAmountHistoryChart from "@/components/charts/ProductAmountHistoryChart"
import useAccess from "@/hooks/useAccess"
import TableList from "@/components/list/tableList/tableList"
import TableListRow from "@/components/list/tableList/tableListRow"
import TableListCell from "@/components/list/tableList/tableListCell"
import { MainSheetHeaderActions } from "@/components/layout/layoutChrome"

const ProductPage = () => {

    const [isRestoringState, setIsRestoringState] = useState(false)

    const { t } = useTranslation()
    const navigate = useNavigate()
    const { displayIfSize } = useWindowDimensions()
    const { targetStorageDocId: storageId } = useTargetStorage()
    const { productId } = useParams<{ productId: string }>()
    
    const { 
        product, 
        productStatus,
        restoreProductAsync,
        isRestoring
    } = useProducts({ storageId, productId })
    const { 
        storage,
        storageStatus,
        isSharedAccessEnabled,
        isActionsRequiredToBeLinkedToTransactions,
        isStockAdjustmentEnabled
    } = useStorages({ storageId })

    const { 
        allCategories,
        getCategoryNamesString,
        categoriesInitialized
    } = useCategories({ storageId })

    const {
        allLocations,
        getLocationNamesString,
        locationsInitialized
    } = useLocations({ storageId })

    const {
        openDialog,
        addToast
    } = useLayout()

    const {
        limitToRoles
    } = useAccess({ storageId: storageId || '' })

    const isLoading = productStatus === FormStatus.LOADING || !product?.sku;

    // Check params and declare safe ones to use in JSX
    if (
        !paramGuardStorage({storageId, storage, storageStatus, navigate}) ||
        !paramGuardProduct({storageId, productId, product, productStatus, navigate})
    ) {
        return <></>
    }
    const safeStorageId = storageId!;
    const safeProductId = productId!;

    
    const { filteredAccessInvitations } = useAccess({ storageId });

    const handleDeleteClick = () => {
        openDialog(`delete_product`, {storageId: safeStorageId, productId: safeProductId})
    }

    const handleRestoreClick = async () => {
        setIsRestoringState(true)
        try {
            await restoreProductAsync({
                storageId: safeStorageId,
                productId: safeProductId
            })
            addToast({
                text: t('productRestored'),
                colorScheme: 'green'
            })
        } catch (error) {
            addToast({
                text: t('failedToRestore'),
                colorScheme: 'red'
            })
        } finally {
            setIsRestoringState(false)
        }
    }

    return (
        <>
            <PageTitle title={product?.name ? `${t('titleProduct')}: ${product.name}` : t('titleProduct')} />
            <MainSheetHeaderActions>
                {product?.isDraft && <Tag colorScheme="yellow">{t('tagDraft')}</Tag>}
                {displayIfSize([DeviceSize.SM]) && product?.sku && !product.isDeleted &&
                    <FloatingButtonGroup id={`product_edit_${product.sku}`} openingButton={<IconButton icon={<MoreVertIcon />} />}>
                        <FloatingButtonGroupButton onClick={() => openDialog('product_history')}>{t('viewHistory')}</FloatingButtonGroupButton>
                        {limitToRoles([AccessRole.EDITOR, AccessRole.OWNER]) &&
                            <>
                                <FloatingButtonGroupButton>
                                    <Link to={`/storage/products/${productId}/edit`}>{t('edit')}</Link>
                                </FloatingButtonGroupButton>
                                <FloatingButtonGroupHr />
                                <FloatingButtonGroupButton>
                                    <div onClick={handleDeleteClick}>{t('delete')}</div>
                                </FloatingButtonGroupButton>
                            </>
                        }
                    </FloatingButtonGroup>
                }
            </MainSheetHeaderActions>
            {product?.isDeleted && (
                <DeletedDocCallout
                    documentType="product"
                    deletionType="soft"
                    action={handleRestoreClick}
                    isRestoring={isRestoring || isRestoringState}
                />
            )}
            {
                product?.categories?.some((categoryId) => {
                    const cat = allCategories?.find((c) => c.id === categoryId);
                    return cat && cat.isDeleted;
                }) && (
                    <Callout type="warning" title={t(`calloutTitleWarning`)}>{t('calloutProductHasDeletedCategory')}</Callout>
                )
            }
            {
                product?.locations?.some((itemId) => {
                    const loc = allLocations?.find((c) => c.id === itemId);
                    return loc && loc.isDeleted;
                }) && (
                    <Callout type="warning" title={t(`calloutTitleWarning`)}>{t('calloutProductHasDeletedLocation')}</Callout>
                )
            }
            <div className={product && product.images && product.images.length > 0 ? `md:flex md:flex-row md:space-x-4` : ``}>
                { product && product.images && product.images.length > 0 &&
                    <Section className="md:w-1/4">
                        <ProductImageGallery images={product?.images || []} productName={product.name} />
                    </Section>
                }
                <div>
                    <Section>
                        <TableList width="fit">
                            {!isLoading ?
                                <>
                                    {!product?.isDraft && (
                                        <TableListRow hoverEffect={false}>
                                            <TableListCell isLabel={true}><b>{t('currentStock')}</b></TableListCell>
                                            <TableListCell>
                                                <AmountTag amount={Number(product?.status?.currentAmount)} unit={product?.unit} lowStockWarning={product?.stockSettings?.lowStockWarning} />
                                            </TableListCell>
                                        </TableListRow>
                                    )}
                                    <TableListRow hoverEffect={false}>
                                        <TableListCell isLabel={true}><b>{t('labelDescription')}</b></TableListCell>
                                        <TableListCell>{product?.description || '-'}</TableListCell>
                                    </TableListRow>
                                    <TableListRow hoverEffect={false}>
                                        <TableListCell isLabel={true}><b>{t('labelCategory')}</b></TableListCell>
                                        <TableListCell>
                                            {categoriesInitialized ? (
                                                product?.categories && product.categories.length > 0 ? (
                                                    <CategoryTags categoryNames={getCategoryNamesString(product.categories).split(', ')} categoryIds={product.categories} storageId={safeStorageId} />
                                                ) : '-'
                                            ) : (
                                                <p className="skeleton short"></p>
                                            )}
                                        </TableListCell>
                                    </TableListRow>
                                    <TableListRow hoverEffect={false}>
                                        <TableListCell isLabel={true}><b>{t('labelLocation')}</b></TableListCell>
                                        <TableListCell>
                                            {locationsInitialized ? (
                                                product?.locations && product.locations.length > 0 ? (
                                                    <LocationTags locationNames={getLocationNamesString(product.locations).split(', ')} locationIds={product.locations} storageId={safeStorageId} />
                                                ) : '-'
                                            ) : (
                                                <p className="skeleton short"></p>
                                            )}
                                        </TableListCell>
                                    </TableListRow>
                                    {/* Responsible user display */}
                                    {product?.stockSettings?.responsibleUserId && isSharedAccessEnabled() && <TableListRow hoverEffect={false}>
                                        <TableListCell isLabel={true}><b>{t('labelResponsiblePerson')}</b></TableListCell>
                                        <TableListCell>
                                            <UserCard
                                                userId={product.stockSettings.responsibleUserId}
                                                size="small"
                                            />
                                        </TableListCell>
                                    </TableListRow>}
                                </> 
                                :
                                <>
                                    <TableListRow hoverEffect={false}>
                                        <TableListCell><p className="skeleton shortest"></p></TableListCell>
                                        <TableListCell>
                                            <div className="grid gap-y-2">
                                                <p className="skeleton long"></p>
                                                <p className="skeleton medium"></p>
                                                <p className="skeleton short"></p>
                                            </div>
                                        </TableListCell>
                                    </TableListRow>
                                    {Array.from({ length: 2 }).map((_, _index) => (
                                        <TableListRow key={_index} hoverEffect={false}>
                                            <TableListCell><p className="skeleton shortest"></p></TableListCell>
                                            <TableListCell><p className="skeleton short"></p></TableListCell>
                                        </TableListRow>
                                    ))}
                                </>
                            }
                        </TableList>
                    </Section>
                    <CustomInputDataDisplay storageId={safeStorageId} categoryIds={product?.categories} product={product ?? undefined} />
                </div>
            </div>
            {!product?.isDraft && (
                <>
                    <SectionDivider />
                    <Section padX={false}>
                        <SectionHeader>
                            <SectionHeaderTitle>
                                <h2>{t(`titleActions`)}</h2>
                            </SectionHeaderTitle>
                            <SectionHeaderActions>
                                {limitToRoles([AccessRole.EDITOR, AccessRole.OWNER, AccessRole.WORKER]) && (isActionsRequiredToBeLinkedToTransactions() ? (
                                    <FloatingButtonGroup id={`top-bar-stock-actions-action`} openingButton={
                                        displayIfSize([DeviceSize.SM]) ?
                                            <IconButton colorScheme="blue" className="visible md:invisible"><AddIcon /></IconButton> :
                                            <Button wideOnMobile design="primary"><AddIcon />{t('titleCreateTransaction')}<ArrowDropDownIcon /></Button>
                                    }>
                                        <FloatingButtonGroupButton to={`/storage/transactions/create/incoming`}>{t('buttonNewIncoming')}</FloatingButtonGroupButton>
                                        <FloatingButtonGroupButton to={`/storage/transactions/create/outgoing`}>{t('buttonNewOutgoing')}</FloatingButtonGroupButton>
                                        {isStockAdjustmentEnabled() && (
                                            <FloatingButtonGroupButton onClick={() => openDialog(`adjust_product_stock`)}>{t('buttonAdjust')}</FloatingButtonGroupButton>
                                        )}
                                    </FloatingButtonGroup>
                                ) : (
                                    <FloatingButtonGroup id={`top-bar-stock-actions-action`} openingButton={
                                        displayIfSize([DeviceSize.SM]) ?
                                            <IconButton colorScheme="blue" className="visible md:invisible"><AddIcon /></IconButton> :
                                            <Button wideOnMobile design="primary"><AddIcon />{t('buttonNewStockAction')}<ArrowDropDownIcon /></Button>
                                    }>
                                        <FloatingButtonGroupButton onClick={() => openDialog(`add_to_stock`)}>{t('buttonAdd')}</FloatingButtonGroupButton>
                                        <FloatingButtonGroupButton onClick={() => openDialog(`remove_from_stock`)}>{t('buttonRemove')}</FloatingButtonGroupButton>
                                        {isStockAdjustmentEnabled() && (
                                            <FloatingButtonGroupButton onClick={() => openDialog(`adjust_product_stock`)}>{t('buttonAdjust')}</FloatingButtonGroupButton>
                                        )}
                                    </FloatingButtonGroup>
                                ))}
                            </SectionHeaderActions>
                        </SectionHeader>
                        <ActionsList storageId={safeStorageId} productId={safeProductId} displayHelper={!product?.isDeleted} />
                    </Section>
                    <SectionDivider />
                    <Section>
                        <SectionHeader>
                            <SectionHeaderTitle>
                                <h2>{t('titleStockHistory')}</h2>
                            </SectionHeaderTitle>
                        </SectionHeader>
                        <ProductAmountHistoryChart 
                            storageId={safeStorageId} 
                            productId={safeProductId}
                            productUnit={product?.unit}
                        />
                    </Section>
                </>
            )}
            <RightSidebar storageId={safeStorageId}>
                <SidebarRow 
                    text={t('buttonViewHistory')} 
                    style={`border`} 
                    onClick={() => openDialog('product_history')}
                    icon={<HistoryIcon />}
                />
                {limitToRoles([AccessRole.EDITOR, AccessRole.OWNER]) && <SidebarGroup title={t('menuProductActions')} allwaysOpen={true}>
                    {!product?.isDeleted && (
                        <>
                            <SidebarRow to={`/storage/products/${safeProductId}/edit`} text={t('buttonEdit')} icon={<EditIcon />} style={`border`} />
                            <SidebarRow onClick={handleDeleteClick} text={t('buttonDelete')} icon={<DeleteIcon />} style={`border`} />
                        </>
                    )}
                    {product?.isDeleted && (
                        <SidebarRow 
                            onClick={handleRestoreClick} 
                            text={isRestoring || isRestoringState ? t('restoringItem') : t('restoreProduct')} 
                            style={`border`} 
                        />
                    )}
                </SidebarGroup>}
            </RightSidebar>
        </>
    )
}

export default ProductPage

